"use client"

import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useTransition,
  memo,
  useRef,
} from "react"
import { useQuery } from "@tanstack/react-query"
import { useRouter, usePathname, useSearchParams } from "next/navigation"
import { searchProducts } from "@/actions/product/product.action"
import { generateFacetDistributions } from "@/utils/composeFilter"
import { makePageTitle } from "@/utils/index"

import { HitsSection } from "@/components/organisms/Product/product-list"
import { PaginationContainer } from "@/components/organisms/PaginationContainer/pagination-container"
import { FacetsDesktop } from "@/components/organisms/Facets/facets-destkop"
import { MobileHeader, Header } from "@/components/organisms/Product/header"
import { ItemNotFound } from "@/components/molecules/NotFound"
import { ReactQueryProvider } from "@/app/contexts/QueryProvider"

export interface Filters {
  categories: string[]
  subcategories: string[]
  rating?: number
  minPrice?: number
  maxPrice?: number
  location?: { coordinates: { lat: number; lon: number }, country: string, state: string, city: string }
  page: number
  sortBy?: string
}

const getInitialFilters = (params: URLSearchParams): Filters => {
  const minPrice = params.has("minPrice") ? Number(params.get("minPrice")) : undefined
  const maxPrice = params.has("maxPrice") ? Number(params.get("maxPrice")) : undefined

  const location = params.has('latitude') && params.has('longitude') ? {
    latitude: Number(params.get('latitude')),
    longitude: Number(params.get('longitude')),
    country: params.get('country') || '',
    state: params.get('state') || '',
    city: params.get('city') || ''
  } : undefined

  return {
    categories: params.getAll("categories[]"),
    subcategories: params.getAll("subcategories[]"),
    rating: params.has("rating") ? Number(params.get("rating")) : undefined,
    minPrice: !isNaN(minPrice!) ? minPrice : undefined,
    maxPrice: !isNaN(maxPrice!) ? maxPrice : undefined,
    location: location ? { coordinates: { lat: location.latitude, lon: location.longitude }, country: location.country, state: location.state, city: location.city } : undefined,
    page: Math.max(1, Number(params.get("page")) || 1),
    sortBy: params.get("sortBy") || undefined,
  }
}

function ProductsListComponent({
  disabledFacets,
  collection,
}: {
  disabledFacets?: string[]
  collection?: any
}) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isMounted, setIsMounted] = useState(false)
  const [_isPending, startTransition] = useTransition()
  const lastUrlRef = useRef("")

  // Initialize component state
  useEffect(() => {
    setIsMounted(true)
    return () => setIsMounted(false)
  }, [])

  // Memoized search parameters
  const searchParamsString = useMemo(() => searchParams.toString(), [searchParams])

  // Products query with error handling
  const productsQuery: any = useQuery({
    queryKey: ["products", searchParamsString],
    queryFn: () => {
      try {
        return searchProducts(getInitialFilters(searchParams))
      } catch (error) {
        console.error("Search failed:", error)
        throw error
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
  })

  // Memoized derived state
  const { products, totalPages, isLoading, isError, isEmpty } = useMemo(() => ({
    products: productsQuery.data?.data?.data?.items ?? [],
    totalPages: productsQuery.data?.data?.data?.pages ?? 0,
    isLoading: productsQuery.isLoading,
    isError: productsQuery.isError,
    isEmpty: !productsQuery.isLoading && !productsQuery.isError &&
      (productsQuery.data?.data?.data?.items?.length ?? 0) === 0
  }), [productsQuery])

  // Generate facet distributions
  const { facetDistribution, independentFacetDistribution } = useMemo(() =>
    generateFacetDistributions(products),
    [products]
  )

  // Optimized filter change handler
  const handleFilterChange = useCallback((newFilters: Partial<Filters>) => {
    if (!isMounted) return

    const currentFilters = getInitialFilters(searchParams)
    const updatedFilters = {
      ...currentFilters,
      ...newFilters,
      page: 1,
      minPrice: validatePrice(newFilters.minPrice),
      maxPrice: validatePrice(newFilters.maxPrice)
    }

    // Create new URLSearchParams efficiently
    const params = new URLSearchParams()

    // Add array values
    updatedFilters.categories.forEach(c => params.append("categories[]", c))
    updatedFilters.subcategories.forEach(s => params.append("subcategories[]", s))

    // Add optional values
    if (updatedFilters.rating) params.set("rating", String(updatedFilters.rating))
    if (updatedFilters.minPrice) params.set("minPrice", String(updatedFilters.minPrice))
    if (updatedFilters.maxPrice) params.set("maxPrice", String(updatedFilters.maxPrice))
    if (updatedFilters.location) {
      params.set("latitude", String(updatedFilters.location.coordinates.lat));
      params.set("longitude", String(updatedFilters.location.coordinates.lon));
      params.set("country", String(updatedFilters.location.country));
      params.set("state", String(updatedFilters.location.state));
      params.set("city", String(updatedFilters.location.city));
    }
    if (updatedFilters.sortBy) params.set("sortBy", updatedFilters.sortBy);

    const newUrl = `${pathname}?${params.toString()}`

    // Only update if URL actually changed
    if (lastUrlRef.current !== newUrl) {
      lastUrlRef.current = newUrl
      startTransition(() => {
        try {
          router.replace(newUrl, { scroll: false })
        } catch (error) {
          console.error('Navigation error:', error)
        }
      })
    }
  }, [searchParams, router, pathname, isMounted])

  // Helper function to validate price values
  const validatePrice = (price?: number): number | undefined => {
    return price && !isNaN(price) && price >= 0 ? price : undefined
  }

  if (!isMounted) {
    return (
      <div className="mx-auto w-full max-w-screen-2xl p-4 md:px-12 md:pb-24 md:pt-4">
        <div className="h-screen w-full bg-background" />
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-screen-2xl p-4 md:px-12 md:pb-24 md:pt-4">
      <Header
        title={makePageTitle(collection, "")}
        totalHits={products.length}
        showFilter
      />

      <hr className="lg:hidden" aria-hidden="true" />

      <MobileHeader
        totalHits={products.length}
        disabledFacets={disabledFacets}
        independentFacetDistribution={independentFacetDistribution}
        facetDistribution={facetDistribution}
        currentFilters={getInitialFilters(searchParams)}
        onFilterChange={handleFilterChange}
      />

      <div className="flex gap-12 md:gap-24">
        <FacetsDesktop
          className="sticky top-[156px] hidden max-h-[70dvh] shrink-0 basis-[192px] overflow-y-auto lg:block"
          disabledFacets={disabledFacets}
          independentFacetDistribution={independentFacetDistribution}
          facetDistribution={facetDistribution}
          currentFilters={getInitialFilters(searchParams)}
          onFilterChange={handleFilterChange}
        />

        <MainContent
          hits={products}
          totalPages={totalPages}
          isLoading={isLoading}
          isError={isError}
          isEmpty={isEmpty}
          queryParams={Object.fromEntries(searchParams.entries())}
        />
      </div>
    </div>
  )
}

const MainContent = memo(function MainContent({
  hits,
  totalPages,
  isLoading,
  isError,
  isEmpty,
  queryParams,
}: {
  hits: any[]
  totalPages: number
  isLoading: boolean
  isError: boolean
  isEmpty: boolean
  queryParams: Record<string, string | string[]>
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-80 w-full animate-pulse rounded-xl bg-muted"
            aria-hidden="true"
          />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="w-full place-items-center">
        <ItemNotFound />
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className="w-full place-items-center">
        <ItemNotFound />
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <HitsSection hits={hits} />
      {totalPages > 1 && (
        <PaginationContainer
          totalPages={totalPages}
          queryParams={queryParams}
        />
      )}
    </div>
  )
})

export function ProductsPage() {
  return (
    <ReactQueryProvider>
      <ProductsListComponent />
    </ReactQueryProvider>
  )
}